import * as ChannelService from '@channel.io/channel-web-sdk-loader'

import { type User } from '@/features/member/member.type'
import { Locales } from '@/utils/language'

import type { BootOption } from '@channel.io/channel-web-sdk-loader'

// Channel Talk 설정 상수
export const CHANNEL_TALK_CONFIG = {
  PLUGIN_KEY: 'c14e5734-4a9b-4c33-a8f4-78bed753f6e8', // UnniGuide 전용 Plugin Key
  WORKFLOWS: {
    [Locales.ZH_HANT_TW]: '791001', // 대만
    [Locales.ZH_HANS_CN]: '791002', // 중국
    [Locales.TH_TH]: '791000', // 태국
    [Locales.EN_US]: '790999', // 그 외 국가
    [Locales.JA_JP]: '790999', // 일본
    [Locales.KO_KR]: '790999', // 한국
  },
} as const

// Channel Talk 언어 매핑
const getChannelLanguage = (locale: Locales): string => {
  const languageMap: Record<Locales, string> = {
    [Locales.KO_KR]: 'ko',
    [Locales.JA_JP]: 'ja',
    [Locales.EN_US]: 'en',
    [Locales.TH_TH]: 'en', // Channel Talk은 태국어 미지원
    [Locales.ZH_HANS_CN]: 'en', // 중국어 간체 미지원
    [Locales.ZH_HANT_TW]: 'en', // 중국어 번체 미지원
  }
  return languageMap[locale] || 'en'
}

// Channel Talk 서비스 인터페이스
export interface ChannelTalkService {
  initialize(): void
  boot(user?: User, locale?: Locales): Promise<void>
  shutdown(): void
  openChat(chatId?: string): void
  openWorkflow(locale: Locales): void
  updateUser(user: User): void
  setPage(page: string): void
  track(eventName: string, properties?: Record<string, any>): void
}

interface DeviceInfo {
  userAgent: string
  platform: string
  browserLanguage: string
  timezone: string
  screenResolution: string
  windowSize: string
}

// 브라우저 디바이스 정보 수집
const getDeviceInfo = (): DeviceInfo => {
  if (typeof window === 'undefined') {
    return {
      userAgent: '',
      platform: '',
      browserLanguage: '',
      timezone: '',
      screenResolution: '',
      windowSize: '',
    }
  }
  try {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      browserLanguage: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${screen.width}x${screen.height}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
    }
  } catch (error) {
    console.error('Failed to get device info:', error)
    return {
      userAgent: '',
      platform: '',
      browserLanguage: '',
      timezone: '',
      screenResolution: '',
      windowSize: '',
    }
  }
}

interface SessionInfo {
  referrer: string
  currentUrl: string
  sessionStartTime: string
}

// 세션 정보 수집
const getSessionInfo = (): SessionInfo => {
  if (typeof window === 'undefined') {
    return {
      referrer: '',
      currentUrl: '',
      sessionStartTime: '',
    }
  }
  try {
    return {
      referrer: document.referrer,
      currentUrl: window.location.href,
      sessionStartTime: new Date().toISOString(),
    }
  } catch (error) {
    console.error('Failed to get session info:', error)
    return {
      referrer: '',
      currentUrl: '',
      sessionStartTime: '',
    }
  }
}

// 사용자 프로필을 Channel Talk 형식으로 변환
const convertUserProfile = (user: User): Record<string, any> => {
  const deviceInfo = getDeviceInfo()
  const sessionInfo = getSessionInfo()

  return {
    // 기본 사용자 정보
    name: user.name || '',
    email: user.loginId || '',
    mobileNumber: user.phoneNumber || '',
    avatarUrl: user.profileImage || '',

    // 추가 커스텀 필드
    userId: user.id?.toString() || '',
    level: user.level?.toString() || '',
    country: user.country || '',
    locale: user.locale || '',

    // 디바이스 정보
    ...deviceInfo,

    // 세션 정보
    ...sessionInfo,

    // 비즈니스 정보 (User 타입에 있는 경우)
    // registrationDate: '',
    // membershipType: '',
    // lastLoginDate: '',
  }
}

// Channel Talk 서비스 클래스
class ChannelTalkServiceImpl implements ChannelTalkService {
  public isInitialized = false
  private currentUser: User | null = null

  initialize(): void {
    if (typeof window === 'undefined') {
      console.warn('Channel Talk can only be initialized in browser environment')
      return
    }

    if (this.isInitialized) {
      console.log('Channel Talk is already initialized')
      return
    }

    try {
      ChannelService.loadScript()
      this.isInitialized = true
      // console.log('Channel Talk SDK loaded successfully')
    } catch (error) {
      console.error('Failed to load Channel Talk SDK:', error)
    }
  }

  async boot(user?: User, locale?: Locales): Promise<void> {
    if (!this.isInitialized) {
      this.initialize()
    }

    if (typeof window === 'undefined') {
      return
    }

    try {
      const channelLanguage = locale ? getChannelLanguage(locale) : 'en'

      const bootOptions: BootOption = {
        pluginKey: CHANNEL_TALK_CONFIG.PLUGIN_KEY,
        hideChannelButtonOnBoot: true, // 채팅 버튼을 숨김
        language: channelLanguage,
        // appearance: 'auto', // 자동 모드
      }

      // 사용자가 로그인한 경우
      if (user?.id && user.id !== 0) {
        const memberId = user.id.toString()
        const profile = convertUserProfile(user)

        // Workflow 분기를 위한 커스텀 필드 추가
        if (locale) {
          profile.targetLocale = locale
        }

        bootOptions.memberId = memberId
        bootOptions.profile = profile

        // console.log('Channel Talk booting with user:', { memberId, profile, locale })
      } else {
        // console.log('Channel Talk booting as anonymous user with locale:', locale)
      }

      ChannelService.boot(bootOptions)
      this.currentUser = user || null
    } catch (error) {
      console.error('Failed to boot Channel Talk:', error)
    }
  }

  shutdown(): void {
    if (!this.isInitialized) {
      return
    }
    if (typeof window === 'undefined') {
      return
    }

    try {
      ChannelService.shutdown()
      this.currentUser = null
      // console.log('Channel Talk shut down successfully')
    } catch (error) {
      console.error('Failed to shutdown Channel Talk:', error)
    }
  }

  openChat(chatId?: string): void {
    if (typeof window === 'undefined') {
      return
    }

    try {
      if (chatId) {
        ChannelService.openChat(chatId)
      } else {
        ChannelService.openChat()
      }
    } catch (error) {
      console.error('Failed to open Channel Talk chat:', error)
    }
  }

  openWorkflow(locale: Locales): void {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const workflowId = CHANNEL_TALK_CONFIG.WORKFLOWS[locale]

      if (workflowId) {
        // Channel Talk openWorkflow API 사용
        ChannelService.openWorkflow(workflowId)
        console.log('Opening Channel Talk workflow:', { locale, workflowId })
      } else {
        // Workflow ID 없으면 일반 채팅으로 폴백
        console.warn('No workflow ID found for locale:', locale)
        ChannelService.openChat()
      }
    } catch (error) {
      console.error('Failed to open workflow:', error)
      // 실패 시 일반 채팅으로 폴백
      try {
        ChannelService.openChat()
      } catch (fallbackError) {
        console.error('Failed to open chat as fallback:', fallbackError)
      }
    }
  }

  updateUser(user: User): void {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const profile = convertUserProfile(user)
      ChannelService.updateUser({
        profile,
        language: user.locale || 'ko',
      })
      this.currentUser = user
      console.log('Channel Talk user updated:', profile)
    } catch (error) {
      console.error('Failed to update Channel Talk user:', error)
    }
  }

  setPage(page: string): void {
    if (typeof window === 'undefined') {
      return
    }

    try {
      ChannelService.setPage(page)
    } catch (error) {
      console.error('Failed to set Channel Talk page:', error)
    }
  }

  track(eventName: string, properties?: Record<string, any>): void {
    if (typeof window === 'undefined') {
      return
    }

    try {
      ChannelService.track(eventName, properties)
    } catch (error) {
      console.error('Failed to track Channel Talk event:', error)
    }
  }

  getCurrentUser(): User | null {
    return this.currentUser
  }

  isUserLoggedIn(): boolean {
    return this.currentUser !== null && this.currentUser.id !== 0
  }

  getWorkflowId(locale: Locales): string | undefined {
    return CHANNEL_TALK_CONFIG.WORKFLOWS[locale]
  }
}

// 싱글톤 인스턴스 생성
const channelTalkService = new ChannelTalkServiceImpl()

// 기본 export
export default channelTalkService

// Named exports
export { channelTalkService }
export type { User as ChannelTalkUser }
